/**
 * Re-exports the canonical IO schema + enums from the API package so the UI
 * can populate <select> options and run client-side validation without
 * duplicating the enum definitions.
 *
 * The import path is relative across the monorepo:
 *   packages/review-ui/src/lib/ioSchema.js
 *   -> packages/api/schemas/io.js
 *
 * The exact field names (snake_case, _meta, _note) are defined there and in
 * `docs/io-schema.md`; the UI must use these names verbatim.
 */

import {
  AdFormat,
  PricingModel,
  FlatFeePeriod,
  FrequencyScope,
  AgencyContact,
  Targeting,
  LineItem,
  Meta,
  IORecord,
  checkBudgetReconciliation,
} from "../../../api/schemas/io.js";

// Enum options for <select> dropdowns. Each entry is { value, label }.
export const AD_FORMAT_OPTIONS = AdFormat.options.map((o) => ({
  value: o,
  label: o,
}));

export const PRICING_MODEL_OPTIONS = PricingModel.options.map((o) => ({
  value: o,
  label: o,
}));

export const FLAT_FEE_PERIOD_OPTIONS = FlatFeePeriod.options.map((o) => ({
  value: o,
  label: o,
}));

export const FREQUENCY_SCOPE_OPTIONS = FrequencyScope.options.map((o) => ({
  value: o,
  label: o,
}));

// Common ISO 4217 currency codes for a currency <select>. Mirrors the set in
// the API schema.
export const CURRENCY_OPTIONS = [
  "USD", "EUR", "GBP", "JPY", "AUD", "CAD", "CHF", "CNY", "SEK", "NZD",
  "MXN", "SGD", "HKD", "NOK", "KRW", "TRY", "INR", "BRL", "RUB", "ZAR",
  "AED", "SAR", "THB", "IDR", "MYR", "PHP", "PLN", "DKK", "CZK", "HUF",
  "ILS", "CLP", "COP", "PEN", "ARS", "VND", "EGP", "NGN", "PKR", "BDT",
].map((c) => ({ value: c, label: c }));

// Re-export the Zod schemas + helpers so consumers can validate inline.
export {
  AdFormat,
  PricingModel,
  FlatFeePeriod,
  FrequencyScope,
  AgencyContact,
  Targeting,
  LineItem,
  Meta,
  IORecord,
  checkBudgetReconciliation,
};

// ---------------------------------------------------------------------------
// Order-level field metadata — drives the ReviewPane field grouping.
// ---------------------------------------------------------------------------
// Each entry: { key, label, type, options?, group }
//   type: 'text' | 'number' | 'date' | 'select'
//   options: array of { value, label } for selects
//   group: heading used to cluster fields in the right pane
export const ORDER_FIELDS = [
  { key: "io_number", label: "IO Number", type: "text", group: "Identification" },
  { key: "io_version", label: "IO Version", type: "text", group: "Identification" },
  { key: "publisher", label: "Publisher", type: "text", group: "Identification" },
  { key: "advertiser_name", label: "Advertiser", type: "text", group: "Identification" },
  { key: "agency", label: "Agency", type: "text", group: "Identification" },
  { key: "agency_contact.name", label: "Agency Contact Name", type: "text", group: "Identification" },
  { key: "agency_contact.email", label: "Agency Contact Email", type: "text", group: "Identification" },
  { key: "campaign_name", label: "Campaign Name", type: "text", group: "Identification" },

  { key: "contract_start_date", label: "Contract Start", type: "date", group: "Dates & Budget" },
  { key: "contract_end_date", label: "Contract End", type: "date", group: "Dates & Budget" },
  { key: "total_gross_budget", label: "Total Gross Budget", type: "number", group: "Dates & Budget" },
  { key: "total_net_budget", label: "Total Net Budget", type: "number", group: "Dates & Budget" },
  { key: "currency", label: "Currency", type: "select", options: CURRENCY_OPTIONS, group: "Dates & Budget" },

  { key: "special_instructions", label: "Special Instructions", type: "text", group: "Notes" },
];

// ---------------------------------------------------------------------------
// Line-item field metadata — same shape, used per line item in ReviewPane.
// ---------------------------------------------------------------------------
export const LINE_ITEM_FIELDS = [
  { key: "line_item_id", label: "Line Item ID", type: "text", group: "Identification" },
  { key: "line_item_name", label: "Line Item Name", type: "text", group: "Identification" },
  { key: "ad_format", label: "Ad Format", type: "select", options: AD_FORMAT_OPTIONS, group: "Buy" },
  { key: "pricing_model", label: "Pricing Model", type: "select", options: PRICING_MODEL_OPTIONS, group: "Buy" },
  { key: "rate", label: "Rate", type: "number", group: "Buy" },
  { key: "units", label: "Units", type: "number", group: "Buy" },
  { key: "flat_fee_rate", label: "Flat Fee Rate", type: "number", group: "Buy" },
  { key: "flat_fee_period", label: "Flat Fee Period", type: "select", options: FLAT_FEE_PERIOD_OPTIONS, group: "Buy" },
  { key: "flat_fee_months", label: "Flat Fee Months", type: "number", group: "Buy" },
  { key: "gross_budget", label: "Gross Budget", type: "number", group: "Budget" },
  { key: "net_budget", label: "Net Budget", type: "number", group: "Budget" },
  { key: "flight_start_date", label: "Flight Start", type: "date", group: "Dates" },
  { key: "flight_end_date", label: "Flight End", type: "date", group: "Dates" },
  { key: "frequency_cap", label: "Frequency Cap", type: "text", group: "Targeting" },
  { key: "creative_dimensions", label: "Creative Dimensions", type: "text", group: "Creative", array: true },
  { key: "creative_specs", label: "Creative Specs", type: "text", group: "Creative" },
  { key: "custom_kpis", label: "Custom KPIs", type: "text", group: "Creative", array: true },
  { key: "third_party_tracking", label: "3rd-Party Tracking", type: "text", group: "Creative", array: true },
  { key: "_note", label: "Note", type: "text", group: "Notes" },
];

// Targeting sub-fields rendered nested under each line item.
export const TARGETING_FIELDS = [
  { key: "geo", label: "Geo", type: "text", group: "Targeting", array: true },
  { key: "geo_detail", label: "Geo Detail", type: "text", group: "Targeting" },
  { key: "audience", label: "Audience", type: "text", group: "Targeting", array: true },
  { key: "intent_data", label: "Intent Data", type: "text", group: "Targeting" },
  { key: "job_title", label: "Job Title", type: "text", group: "Targeting", array: true },
  { key: "device", label: "Device", type: "text", group: "Targeting", array: true },
  { key: "frequency_scope", label: "Frequency Scope", type: "select", options: FREQUENCY_SCOPE_OPTIONS, group: "Targeting" },
  { key: "daypart", label: "Daypart", type: "text", group: "Targeting" },
  { key: "brand_safety", label: "Brand Safety", type: "text", group: "Targeting" },
  { key: "placement", label: "Placement", type: "text", group: "Targeting" },
  { key: "send_schedule", label: "Send Schedule", type: "text", group: "Targeting" },
];

// ---------------------------------------------------------------------------
// Helpers for dotted-path get/set on nested IO records.
// ---------------------------------------------------------------------------

/** Get a value by dotted path (e.g. "line_items.2.gross_budget"). */
export function getByPath(obj, path) {
  if (!obj || !path) return undefined;
  return path.split(".").reduce((acc, key) => {
    if (acc == null) return undefined;
    return acc[key];
  }, obj);
}

/** Set a value by dotted path on a shallow-cloned object, returning the clone. */
export function setByPath(obj, path, value) {
  if (!path) return obj;
  const parts = path.split(".");
  const next = Array.isArray(obj) ? [...obj] : { ...obj };
  let cursor = next;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    const child = cursor[key];
    cursor[key] = Array.isArray(child) ? [...child] : { ...child };
    cursor = cursor[key];
  }
  cursor[parts[parts.length - 1]] = value;
  return next;
}

/**
 * Read a confidence score for a dotted field path from _meta.confidence.
 * The confidence map uses glob-style keys like "line_items[*].rate" or
 * plain top-level keys like "total_gross_budget". We try the exact path,
 * then a globbed variant (line_items.2.rate -> line_items[*].rate).
 */
export function confidenceForPath(ioRecord, path) {
  const conf = ioRecord?._meta?.confidence;
  if (!conf || typeof conf !== "object") return null;
  if (path in conf) return conf[path];
  // line_items.2.gross_budget -> line_items[*].gross_budget
  const globbed = path.replace(/\.(\d+)\./, ".*.");
  if (globbed in conf) return conf[globbed];
  // line_items.2 -> line_items[*]
  const arrayGlob = path.replace(/\.(\d+)$/, ".*");
  if (arrayGlob in conf) return conf[arrayGlob];
  return null;
}